import React, { useState, useEffect, useRef } from "react";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import { Paragraph } from "@arctheme-components/elements/Paragraph/Paragraph";
import {Checkbox}  from "@arctheme-components/elements/Checkbox/Checkbox";
import { TextField } from "@arctheme-components/elements/TextField/TextField";
import { TextArea } from "@arctheme-components/elements/TextArea/TextArea";
import { Headings } from "@arctheme-components/elements/Headings/Headings";
import { useGorgiasSubmit } from "@arctheme-hooks/useGorgiasFormSubmit";
import helpers from "@project-stories/../../js/helpers";
import { WaveDividerVertical } from "@src/stories/client/WaveDividerVertical/WaveDividerVertical";
import { WaveDivider } from "@src/stories/client/WaveDivider/WaveDivider";

const TwoColumnLayout = ({ blocks, settings }) => {
  const isMobile = useMobileBreakpoint(settings.mobile_breakpoint);
  const { section } = settings;
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    acceptTerms: false,
    file: null
  });
  const [characterCount, setCharacterCount] = useState(0);
  const maxCharacterCount = 100; // This could be a configurable setting
  const containerRef = useRef(null);
  const formElementRef = useRef(null);

  const { isLoading, error, success, submitForm } = useGorgiasSubmit(
      `${helpers.getDomain()}/apps/zeroco/gorgias/contact`
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData((prevData) => ({
        ...prevData,
        [name]: checked,
      }));
    } else if (name === 'message') {
      setCharacterCount(value.length);
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFormData((prevData) => ({
        ...prevData,
        file: e.target.files[0],
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create FormData object to handle file uploads
    const submitData = new FormData();
    Object.keys(formData).forEach(key => {
      if (key === 'file' && formData.file) {
        submitData.append(key, formData.file);
      } else if (key !== 'file') {
        submitData.append(key, formData[key]);
      }
    });
    
    submitForm(submitData);
  };

  // Find detail block for contact information
  const detailsBlock = blocks?.find(block => block.type === "details");
  const hulkFormBlock = blocks?.find(block => block.type === "hulk_form");
  const detailsBlocks = blocks?.filter(block => block.type === "details");

  const renderBlock = (block, index) => {
    const commonClasses = `
      text-${block.settings.content_alignment_mobile} lg:text-${block.settings.content_alignment_desktop}
      pt-[${block.settings.padding_top_mobile}px] lg:pt-[${block.settings.padding_top_desktop}px]
      pb-[${block.settings.padding_bottom_mobile}px] lg:pb-[${block.settings.padding_bottom_desktop}px]
      px-[${block.settings.padding_left_right_mobile}px] lg:px-[${block.settings.padding_left_right_desktop}px]
    `;
    switch (block.type) {
      case "form":
        return (
          <div>
            {success ? (
              <div className="success-message p-6 bg-green-50 rounded-md">
                <p className="text-green-700">Thank you for your message. We'll be in touch with you shortly.</p>
              </div>
            ) : (
              <form
                id="contact-us-form"
                onSubmit={handleSubmit}
                className="space-y-[24px]"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="floating-input">
                    <TextField
                      name="name"
                      placeholder="First Name *"
                      classes="floating-input w-full"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="floating-input">
                    <TextField
                      name="phone"
                      placeholder="Your Phone Number *"
                      classes="floating-input w-full"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                <div className="floating-input">
                  <TextField
                    name="email"
                    placeholder="Your Email *"
                    classes="floating-input w-full"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="floating-input relative">
                  <TextArea
                    placeholder="Your Message *"
                    name="message"
                    classes="floating-input w-full min-h-[120px]"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                  <div className="text-right text-xs text-gray-500 mt-1">
                    {characterCount}/{maxCharacterCount}
                  </div>
                </div>
                
                <div className="file-upload">
                  <label className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md cursor-pointer bg-white hover:bg-gray-50">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span>Upload a file</span>
                    <input 
                      type="file" 
                      className="hidden" 
                      onChange={handleFileChange} 
                    />
                  </label>
                  {formData.file && (
                    <p className="mt-2 text-sm text-gray-600">{formData.file.name}</p>
                  )}
                </div>
                
                <div className="flex items-start space-x-2">
                  <Checkbox 
                    id="acceptTerms" 
                    name="acceptTerms"
                    checked={formData.acceptTerms}
                    onChange={handleChange}
                    label={
                      <span dangerouslySetInnerHTML={{ __html: block.settings?.terms_message }} />
                    } 
                    required
                  />
                </div>
                
                {error && <p className="errors text-red-500">{error}</p>}
                
                <button
                  type="submit"
                  className="button button-primary w-full"
                  disabled={isLoading}
                >
                  {isLoading ? "Submitting..." : block.settings?.submit_button_text || "Submit your message"}
                </button>
              </form>
            )}
          </div>
        );
      case "header":
        return (
          <div className={`space-y-[8px] flex flex-col text-left`}>
            <div style={{ color: block.settings?.heading_color || "#F2610D" }}>
              <Headings
                type={block.settings?.heading_style || "h2"}
                text={block.settings?.heading || "Contact us"}
              />
            </div>

            {block.settings?.tagline && (
              <Paragraph
                text={block.settings?.tagline}
                type={block.settings?.tagline_style || "b2"}
              />
            )}
            {block.settings?.description && (
              <Paragraph
                text={block.settings?.description}
                type={block.settings?.description_style || "b2"}
                maxChars={99999}
              />
            )}
          </div>
        );
      case "hulk_form":
        if (!block.settings?.hulk_form_id) return;

        return (
          <div ref={containerRef} />
        )

      case "image":
        const imageUrl =
          isMobile && block.settings?.image_mobile
            ? block.settings.image_mobile
            : block.settings?.image;

        return (
          <div className={`contact-us__image w-full h-full relative overflow-hidden`}>
            <WaveDividerVertical
              settings={{
                wave_offset: 0,
                wave_width: 60,
                wave_color: "#ffffff",
                wave_opacity: 1,
                wave_style: "curved",
                flip_horizontally: true,
                flip_vertically: false,
                position: "left",
                hide_on_mobile: true,
                z_index: 1,
                render_type: "all",
                custom_id: "image-divider",
                custom_class: "!absolute",
                show_section_borders: false,
                background_color: "transparent",
              }}
            />

            <img
              key={index}
              className="relative object-cover h-full w-full"
              src={imageUrl}
              alt="Contact us image"
              loading="lazy"
            />

            <WaveDivider
              settings={{
                wave_offset: 0,
                wave_height: 60,
                wave_color: "#ffffff",
                wave_opacity: 1,
                wave_style: "wavy",
                flip_horizontally: false,
                flip_vertically: false,
                position: "top",
                hide_on_mobile: false,
                z_index: 1,
                render_type: "mobile-only",
                custom_id: "image-divider-mobile",
                custom_class: "!absolute bottom-0 lg:hidden",
                show_section_borders: false,
                background_color: "transparent",
              }}
            />
          </div>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const hulkFormId = hulkFormBlock?.settings?.hulk_form_id;
    const hulkForm = document.getElementById(hulkFormId);

    function appendHulkForm() {
      if (hulkForm && containerRef.current && !containerRef.current.contains(hulkForm)) {
        formElementRef.current = hulkForm;
        containerRef.current.appendChild(hulkForm);
        hulkForm.style.display = 'block';
      }
    }

    appendHulkForm();

    // Set up a MutationObserver to re-append if removed
    const observer = new MutationObserver(() => {
      // If the hulkForm is not in the container, re-append it
      if (hulkForm) {
        appendHulkForm();
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current, { childList: true });
    }

    return () => {
      observer.disconnect();
    };
  }, [hulkFormBlock]);

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2">
        <div className="contact-us__column space-y-[48px] flex flex-col pb-8 px-6 lg:px-12 lg:py-16 max-md:order-2">
          {blocks &&
            blocks
              .filter((block) => block.settings.column === 1 || !block.settings.column)
              .sort((a, b) => a.settings.sort_order - b.settings.sort_order)
              .map((block, index) => {
                return renderBlock(block, index);
              })}

          {!!detailsBlocks?.length && (
            <div className={`contact-details-footer flex items-start justify-center mt-10 flex-wrap gap-x-8`}>
              {detailsBlocks.map((detailsBlock, index) => {
                let linkType = detailsBlock.settings?.link_type || "";
                let href = "";
    
                if (linkType === "phone") {
                  href = `tel:${detailsBlock.settings?.tagline}`;
    
                } else if (linkType === "email") {
                  href = `mailto:${detailsBlock.settings?.tagline}`;
                } 
    
                return (
                  <div
                    key={`detailsBlock_${index}`}
                    className={`contact-item py-4 sm:flex-1 max-sm:max-w-[200px] max-sm:min-w-[144px]`}
                  >
                    <div
                      className="text-2xl"
                      style={{ color: detailsBlock.settings?.icon_color }}
                    >
                      <span className={detailsBlock.settings?.icon}></span>
                    </div>
      
                    <Paragraph 
                      type={`${detailsBlock.settings?.title_style} mb-2`}
                      text={detailsBlock.settings?.title}
                    />
      
                    {href
                      ? <a href={href} className="hover:underline">
                          <Paragraph 
                            type={detailsBlock.settings?.tagline_style}
                            text={detailsBlock.settings?.tagline}
                          />
                        </a>
    
                      : <Paragraph 
                          type={detailsBlock.settings?.tagline_style}
                          text={detailsBlock.settings?.tagline}
                        />
                    }
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div
          className="contact-us__column max-md:order-1"
          style={{ backgroundColor: section.column_2_color }}
        >
          {blocks &&
            blocks
              .filter((block) => block.settings.column === 2)
              .sort((a, b) => a.settings.sort_order - b.settings.sort_order)
              .map((block, index) => {
                return renderBlock(block, index);
              })}
        </div>
      </div>

      
      <WaveDivider
        settings={{
          wave_offset: 0,
          wave_height: 50,
          wave_color: section.bottom_wave_color,
          wave_opacity: 1,
          wave_style: "rainbow",
          flip_horizontally: false,
          flip_vertically: false,
          position: "bottom",
          hide_on_mobile: false,
          z_index: 2,
          custom_id: "image-divider-mobile",
          custom_class: "mt-[-28px] lg:mt-[-50px]",
          show_section_borders: false,
          background_color: "transparent",
        }}
      />
    </div>
  );
};

export default TwoColumnLayout;
